



















import { computed, defineComponent, onBeforeUnmount, ref } from "@vue/composition-api";
import { ContentPriceSubjectTypeEnum } from "@scrile/api-provider/dist/api/ContentPriceProvider";
import { CreditCardAssignment } from "@scrile/api-provider/dist/api/CreditCardAssignmentsProvider";
import { RecurringBillingScheduleSubjectTypeEnum } from "@scrile/api-provider/dist/api/RecurringBillingScheduleProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import useAuthorisedOnly from "@/hooks/useAuthorisedOnly";
import useAuthUser from "@/hooks/useAuthUser";
import useSystemSettings from "@/hooks/useSystemSettings";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { getMainCurrencyCode } from "@/lib/moneyHelpers";
import getIntegration from "@/payments/integrations";
import { DefaultPaymentData } from "@/payments/integrations/types";
import { OnStartPurchaseData, PurchaseResponse, SubscriptionResponse } from "@/payments/types";
import { EVENTS } from "@/const";
import ModalView from "@/components/ModalView";

export default defineComponent({
  name: "PurchaseManager",
  components: { ModalView },
  setup() {
    const { authUserStrict } = useAuthUser();
    const show = ref(false);
    const loading = ref(false);
    const processingRequest = ref(false);
    const { creditCardAllowed, currentPaymentSystem } = useSystemSettings();
    const pricePackageId = ref<string>("");
    const price = ref("");
    const purchaseSubject = ref<Subject<ContentPriceSubjectTypeEnum> | null>(null);
    const subscriptionSubject = ref<Subject<RecurringBillingScheduleSubjectTypeEnum> | null>(null);

    const integrationModule = computed(() => getIntegration(currentPaymentSystem.value));
    const forceShowUI = computed(() => integrationModule.value?.forceShowUI ?? false);

    const closeOnError = ref(false);
    const closeModal = (payload: PurchaseResponse | SubscriptionResponse) => {
      emitter.emit(!subscriptionSubject.value ? EVENTS.PAYMENT_PURCHASED : EVENTS.PAYMENT_SUBSCRIBED, payload);
      pricePackageId.value = "";
      price.value = "";
      show.value = false;
      subscriptionSubject.value = null;
      closeOnError.value = false;
    };

    const onClose = () => {
      if (processingRequest.value) return;
      closeModal({
        status: false,
        subjectPurchased: false,
      });
    };

    const startPurchase = async (data?: any) => {
      try {
        processingRequest.value = true;
        if (navigator.userAgent.includes("Instagram")) {
          emitter.emit(EVENTS.STATUS_SHOW, { text: $t("Please, open the browser page to buy it.") });
          throw new Error("Please, open the browser page to buy it.");
        }
        if (!pricePackageId.value || !currentPaymentSystem.value) {
          throw new Error("Cant find payment information");
        }
        let payload: any;
        if (subscriptionSubject.value) {
          payload = await integrationModule.value.subscribe(
            {
              currency: getMainCurrencyCode(),
              paymentSystemId: currentPaymentSystem.value.id,
              subject: subscriptionSubject.value,
            },
            data
          );
        } else {
          const purchaseData: DefaultPaymentData = {
            currency: getMainCurrencyCode(),
            paymentSystemId: currentPaymentSystem.value.id,
            pricePackageId: pricePackageId.value,
          };
          if (purchaseSubject.value && currentPaymentSystem.value.type === "netbilling") {
            purchaseData.subject = purchaseSubject.value;
          }
          payload = await integrationModule.value.purchase(purchaseData, data);
        }
        closeModal(payload);
      } finally {
        processingRequest.value = false;
      }
    };

    const onSubmit = (data?: any) =>
      startPurchase(data).catch(e => {
        // eslint-disable-next-line no-console
        console.error(e);
        if (closeOnError.value) {
          closeModal({
            status: false,
            subjectPurchased: false,
            error: e,
          });
        }
      });

    const onStartPurchase = useAuthorisedOnly(
      async ({
        pricePackageId: newPricePackageId,
        price: newPrice = "",
        subscriptionSubject: newSubscriptionSubject,
        purchaseSubject: newPurchaseSubject,
      }: OnStartPurchaseData) => {
        price.value = newPrice;
        pricePackageId.value = newPricePackageId;
        subscriptionSubject.value = newSubscriptionSubject ?? null;
        purchaseSubject.value = newPurchaseSubject ?? null;
        try {
          loading.value = true;
          if (!forceShowUI.value) {
            let cardsAssignmentData: CreditCardAssignment[] = [];
            if (creditCardAllowed.value) {
              cardsAssignmentData = await providers.CreditCardAssignmentsProvider.findAll({
                userId: authUserStrict.value.id,
              });
            }
            if (cardsAssignmentData.length > 0) {
              try {
                await startPurchase();
              } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
                closeModal({
                  status: false,
                  subjectPurchased: false,
                  error: e,
                });
              }
              return;
            }
          }
          show.value = true;
        } finally {
          loading.value = false;
        }
      }
    );
    emitter.on<any>(EVENTS.PAYMENT_START_PURCHASE, onStartPurchase);

    onBeforeUnmount(() => {
      emitter.off<any>(EVENTS.PAYMENT_START_PURCHASE, onStartPurchase);
    });

    return {
      loading,
      show,
      processingRequest,
      integrationModule,
      price,
      currentPaymentSystem,
      closeOnError,
      pricePackageId,
      purchaseSubject,
      subscriptionSubject,
      onSubmit,
      onClose,
    };
  },
});
